import React from 'react';
import { reportError } from '../utils/errorReporter';

/**
 * Top-level React ErrorBoundary that ships render-time errors to /api/client-errors
 * and shows a Tactical-Brutalism fallback with a reload option.
 */
export default class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorMessage: '' };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, errorMessage: error?.message || 'Something went wrong' };
  }

  componentDidCatch(error, info) {
    reportError({
      message: error?.message || 'React render error',
      stack: error?.stack,
      componentStack: info?.componentStack,
      type: 'react_render',
    });
  }

  handleReload = () => {
    try { window.location.reload(); } catch { /* ignore */ }
  };

  handleHome = () => {
    try { window.location.assign('/'); } catch { /* ignore */ }
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0F0F10] text-white p-6" data-testid="app-error-boundary">
        <div className="max-w-md w-full bg-neutral-900 border-2 border-red-500 rounded-xl p-6 shadow-[6px_6px_0px_#D95800]">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-red-500 text-black rounded-lg flex items-center justify-center font-black border-2 border-black">!</div>
            <h1 className="text-xl font-black uppercase tracking-wider">Something Broke</h1>
          </div>
          <p className="text-neutral-300 text-sm mb-1">
            The app hit an unexpected error. Our team has been notified automatically.
          </p>
          <p className="text-neutral-500 text-xs font-mono mt-3 mb-5 break-words" data-testid="app-error-message">
            {this.state.errorMessage?.slice(0, 240)}
          </p>
          <div className="flex gap-2">
            <button
              onClick={this.handleReload}
              className="flex-1 h-11 bg-[#D95800] text-black font-black rounded-lg border-2 border-black hover:translate-y-[-1px] active:translate-y-0 transition-transform"
              data-testid="app-error-reload"
            >
              Reload
            </button>
            <button
              onClick={this.handleHome}
              className="flex-1 h-11 bg-neutral-800 text-white font-bold rounded-lg border-2 border-neutral-700 hover:bg-neutral-700 transition-colors"
              data-testid="app-error-home"
            >
              Go Home
            </button>
          </div>
        </div>
      </div>
    );
  }
}
